import { useState, useEffect, useCallback, useRef } from 'react'
import { authService } from '../services/authService'
import { registrationService } from '../services/registrationService'
import { queryClient } from '../lib/queryClient'
import { resetSessionExpiredSignal } from '../services/sessionExpiry'

// error_code values returned by POST /api/v1/auth/refresh when
// rotation fails. Mirrors the backend constants in
// ``api/app/modules/auth/api/routes.py``.
export const REFRESH_ERROR_CODE = Object.freeze({
  TOKEN_REUSE_DETECTED: 'token_reuse_detected',
  REFRESH_EXPIRED: 'refresh_expired',
  REFRESH_INVALID: 'refresh_invalid',
})

// Banner text shown when a session is force-revoked due to
// refresh-token reuse detection. Pulled into a constant so the
// frontend tests assert on the literal without duplicating it.
export const REFRESH_REVOKED_BANNER_MESSAGE =
  'Your session was revoked for security. Please log in again.'

// sessionStorage key used to carry the banner message across the hard
// ``window.location.href = '/login'`` redirect performed by the
// token-reuse-detected branch. React state is wiped by a hard
// navigation, so without this the banner would never show up on the
// post-redirect Login page. sessionStorage is scoped to the current
// tab and clears on tab close — safer than localStorage and avoids
// leaking between sessions.
const SESSION_REVOKED_STORAGE_KEY = 'auth.sessionRevokedMessage'

function readPersistedRevokedMessage() {
  try {
    return globalThis.sessionStorage?.getItem(SESSION_REVOKED_STORAGE_KEY) ?? null
  } catch {
    // SecurityError / quota errors — treat as no persisted banner.
    return null
  }
}

function writePersistedRevokedMessage(value) {
  try {
    if (value == null) {
      globalThis.sessionStorage?.removeItem(SESSION_REVOKED_STORAGE_KEY)
    } else {
      globalThis.sessionStorage?.setItem(SESSION_REVOKED_STORAGE_KEY, value)
    }
  } catch {
    /* silent: storage failures don't break the auth flow */
  }
}

/**
 * Manages session lifecycle: initial auth check, login, logout, refresh.
 * Extracted from AuthContext for single-responsibility.
 *
 * @param {object} options
 * @param {function} options.onSessionData - called with session data when auth succeeds
 * @param {function} options.onMfaChallenge - called with session data when MFA is pending
 * @param {function} options.onMfaLogin - called with login data when login returns mfa_required
 * @param {function} options.onLogout - called to reset external state on logout
 */
export function useSessionManager({ onSessionData, onMfaChallenge, onMfaLogin, onLogout }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const sessionExpiredHandledRef = useRef(false)
  // Banner message shown when the refresh endpoint returns
  // ``error_code=token_reuse_detected``. ``null`` when no banner is
  // active. The AuthContext exposes this in its value object so any
  // surface (login screen, app shell) can render it.
  //
  // Hydrate from sessionStorage on mount so the banner survives the
  // ``window.location.href = '/login'`` hard redirect in the
  // reuse-detected branch below.
  const [sessionRevokedMessage, setSessionRevokedMessage] = useState(
    readPersistedRevokedMessage,
  )

  /**
   * Fetch the current session payload, update local user state, and
   * **return the freshly fetched data** so callers can observe
   * session fields (e.g. ``force_password_change``) without waiting
   * for the deferred React state commit.
   *
   * The prior implementation only side-effected via ``setUser``.
   * Callers that needed to react to a flipped session flag
   * immediately after ``await checkAuth()`` (notably ``Login.tsx``
   * post-login redirect logic) had to wait for the setState to
   * commit before AuthContext propagated the new value. Returning
   * the data here lets those callers branch synchronously on the
   * fresh payload, eliminating the React 18 batched-render race
   * that produced the ``force_password_change`` regressions.
   *
   * Returns the session data on success, ``null`` on any failure
   * (auth check failed or MFA-pending — the previous behaviour
   * cleared the user in both cases).
   */
  const checkAuth = useCallback(async () => {
    try {
      const data = await authService.getSession()
      const isMfaPending = onMfaChallenge(data)
      if (isMfaPending) {
        setUser(null)
        return null
      }
      sessionExpiredHandledRef.current = false
      resetSessionExpiredSignal()
      setUser(data)
      onSessionData(data)
      return data
    } catch {
      setUser(null)
      onLogout()
      return null
    } finally {
      setLoading(false)
    }
  }, [onSessionData, onMfaChallenge, onLogout])

  useEffect(() => {
    checkAuth()
  }, [])

  /**
   * Submit login credentials and refresh the session.
   *
   * When the MFA branch does not fire, the returned object is the
   * ``LoginResponse`` body augmented with a ``session`` field
   * carrying the fresh ``/auth/session`` payload (so callers can
   * read ``session.force_password_change`` without waiting for the
   * React state commit). The ``session`` field is ``null`` if the
   * session refresh failed for any reason; callers should treat
   * that as the legacy "unknown" case and proceed via the existing
   * redirect chain.
   */
  const login = useCallback(async (email, password) => {
    const data = await registrationService.login(email, password)
    if (data.mfa_required) {
      onMfaLogin(data)
      return data
    }
    const session = await checkAuth()
    return { ...data, session }
  }, [checkAuth, onMfaLogin])

  const refreshUser = useCallback(async () => {
    try {
      const data = await authService.getSession()
      setUser(data)
    } catch (error) {
      console.error('Failed to refresh user session:', error)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await authService.logout()
    } catch {
      // ignore errors
    } finally {
      queryClient.clear()
      setUser(null)
      onLogout()
      window.location.href = '/login'
    }
  }, [onLogout])

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredHandledRef.current) return
    sessionExpiredHandledRef.current = true
    queryClient.clear()
    setUser(null)
    onLogout()
    if (typeof window !== 'undefined' && window.location?.pathname !== '/login') {
      window.location.href = '/login'
    }
  }, [onLogout])

  /**
   * Rotate the refresh token via POST /api/v1/auth/refresh and react
   * to the discriminated error contract.
   *
   * Reactions:
   *   - ``token_reuse_detected`` — set the revocation banner, clear
   *     the in-memory session, and redirect to /login. The browser
   *     cookies were already cleared by the server response. The
   *     banner stays in state so the login screen can display it.
   *   - ``refresh_expired`` — silently clear cookies + redirect to
   *     /login (the cookie clear was server-driven; we just bounce
   *     the user).
   *   - ``refresh_invalid`` — same silent clear + redirect.
   *   - ``error.errorCode === undefined`` (legacy backend) — fall
   *     back to the legacy behaviour (silent clear + redirect).
   *
   * Returns the refresh response on success, ``null`` on failure
   * (the caller usually does not need the success body — the cookies
   * are what matter).
   */
  const refreshSession = useCallback(async () => {
    try {
      return await authService.refresh()
    } catch (error) {
      const code = error?.errorCode
      if (code === REFRESH_ERROR_CODE.TOKEN_REUSE_DETECTED) {
        setSessionRevokedMessage(REFRESH_REVOKED_BANNER_MESSAGE)
        // Persist before the hard redirect wipes React state (see
        // SESSION_REVOKED_STORAGE_KEY note above).
        writePersistedRevokedMessage(REFRESH_REVOKED_BANNER_MESSAGE)
        queryClient.clear()
        setUser(null)
        onLogout()
        window.location.href = '/login'
        return null
      }
      // refresh_expired, refresh_invalid, or legacy/unknown error —
      // silent re-login flow: clear in-memory state and redirect.
      queryClient.clear()
      setUser(null)
      onLogout()
      window.location.href = '/login'
      return null
    }
  }, [onLogout])

  const clearSessionRevokedMessage = useCallback(() => {
    setSessionRevokedMessage(null)
    writePersistedRevokedMessage(null)
  }, [])

  return {
    user,
    loading,
    login,
    logout,
    checkAuth,
    refreshUser,
    refreshSession,
    handleSessionExpired,
    sessionRevokedMessage,
    clearSessionRevokedMessage,
    isAuthenticated: !!user,
  }
}
